import type { Vec3 } from "../engine/math.js";
import type { CameraCollisionProbe } from "../engine/camera.js";

export const AIM_QUERY = Object.freeze({ range: 80, step: 0.3, refinements: 7, radius: 0.015 });
export interface GameplayAim { origin: Vec3; direction: Vec3; point: Vec3; distance: number; hit: boolean }

/** Reticle target, reusable by held tools. Uses the same conservative world proxies as the boom. */
export function queryGameplayAim(origin: Vec3, direction: Vec3, probe: CameraCollisionProbe, range: number = AIM_QUERY.range): GameplayAim {
  const length = Math.hypot(...direction);
  const valid = origin.every(Number.isFinite) && direction.every(Number.isFinite) && length > 1e-8;
  const start: Vec3 = valid ? [...origin] : [0, 0, 0];
  const forward: Vec3 = valid ? [direction[0] / length, direction[1] / length, direction[2] / length] : [0, 0, 1];
  const maximum = Number.isFinite(range) ? Math.max(0, Math.min(AIM_QUERY.range, range)) : AIM_QUERY.range;
  const at = (distance: number): Vec3 => [start[0] + forward[0] * distance, start[1] + forward[1] * distance, start[2] + forward[2] * distance];
  const blocked = (distance: number) => probe(...at(distance), AIM_QUERY.radius);
  let distance = maximum;
  let hit = false;
  let previous = 0;
  for (let i = 0; i <= Math.ceil(maximum / AIM_QUERY.step); i++) {
    const current = Math.min(maximum, i * AIM_QUERY.step);
    if (blocked(current)) {
      let low = previous;
      let high = current;
      for (let j = 0; j < AIM_QUERY.refinements; j++) {
        const mid = (low + high) / 2;
        if (blocked(mid)) high = mid; else low = mid;
      }
      distance = low;
      hit = true;
      break;
    }
    previous = current;
  }
  return { origin: start, direction: forward, point: at(distance), distance, hit };
}

/** Converge from the actual hand socket, with a finite fallback for a coincident target. */
export function directionToAimPoint(origin: Vec3, target: Vec3, fallback: Vec3): Vec3 {
  const difference: Vec3 = [target[0] - origin[0], target[1] - origin[1], target[2] - origin[2]];
  const length = Math.hypot(...difference);
  return Number.isFinite(length) && length > 0.001
    ? [difference[0] / length, difference[1] / length, difference[2] / length] : [...fallback];
}
